import { useContext, useEffect, useState } from 'react';

import cookie from 'cookie';
import { useLocation, useNavigate } from 'react-router-dom';

import { AuthContext } from '@App/contexts/Auth';
import { getEmailFromUrl } from '@Shared/helpers';

import { LoaderProgress } from '../LoaderProgress';

const ApplicationFormPages = [
    '/eligibility/loan-details',
    '/eligibility/applicants',
    '/eligibility/applicants2',
    '/eligibility/property-details',
    '/eligibility/outgoings',
    '/eligibility/almost-complete',
    '/eligibility/reject',
    '/eligibility/income',
    '/eligibility/income2',
    '/eligibility/create-account',
    '/eligibility/loan-preferences',
    '/eligibility/loan-preferences/homepage',
    '/eligibility/loan-preferences/questions',
    '/eligibility/loan-preferences/book-call',
    '/eligibility/loan-preferences/book-call/confirmation',
    '/eligibility/loan-preferences/book-call/calendar',
    '/eligibility/',
    '/eligibility',
    '/',
];

const Protected = (props: { children: any }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const fallback = '/login';
    const { isLoggedIn, logOut, updateFetchApplicationDataFlag } =
        useContext(AuthContext);
    const [waiting, setWaiting] = useState(true);

    useEffect(() => {
        const checkAuthAndNavigate = async () => {
            const cookies = cookie.parse(document.cookie);
            const appInfo = cookies.application
                ? JSON.parse(cookies.application)
                : {};

            // Check URL params as fallback if cookie doesn't exist
            // handles case with + in email
            let emailFromUrl = '';
            let applicationIdFromUrl = '';
            if (!appInfo.applicationId || !appInfo.emailAddress) {
                emailFromUrl = getEmailFromUrl(location.search);
                const urlParams = new URLSearchParams(location.search);
                applicationIdFromUrl = urlParams.get('applicationId') || '';
            }

            // If URL params exist but cookie doesn't, set the cookie
            if (emailFromUrl && applicationIdFromUrl && !cookies.application) {
                const cookieData = {
                    applicationId: applicationIdFromUrl,
                    emailAddress: emailFromUrl,
                };
                document.cookie = cookie.serialize(
                    'application',
                    JSON.stringify(cookieData),
                    {
                        path: '/',
                        domain: window._env_.SELINA_DOMAIN,
                    },
                );
                // Update appInfo to use URL params
                Object.assign(appInfo, cookieData);
            }

            const storedApplicationId =
                localStorage.getItem('user.applicationId');

            /* Logs the user out to prevent accessing a previous application.
               Ensures applicationId is present in both localStorage and cookies to avoid unnecessary logouts in edge cases.
               Edge cases include scenarios where cookies are missing or the applicationId is null in localStorage,
               which can occur for users with multiple accounts or sign in on different devices. */

            if (
                storedApplicationId &&
                storedApplicationId !== 'null' &&
                appInfo.applicationId &&
                appInfo.applicationId !== storedApplicationId
            ) {
                logOut();
            }

            await updateFetchApplicationDataFlag();

            const loggedIn = await isLoggedIn();

            if (loggedIn) {
                setWaiting(false);
            } else if (!loggedIn && location.pathname !== fallback) {
                const hasApplicationInfo =
                    cookies.application ||
                    (emailFromUrl && applicationIdFromUrl);

                if (!hasApplicationInfo) {
                    navigate(fallback, { replace: true });
                    return;
                }

                const appInfo = cookies.application
                    ? JSON.parse(cookies.application)
                    : {
                          applicationId: applicationIdFromUrl,
                          emailAddress: emailFromUrl,
                      };

                if (
                    ApplicationFormPages.includes(location.pathname) &&
                    appInfo?.applicationId
                ) {
                    setWaiting(false);
                } else {
                    navigate(fallback, { replace: true });
                }
            }
        };

        checkAuthAndNavigate();
    }, [
        isLoggedIn,
        navigate,
        fallback,
        location.pathname,
        location.search,
        logOut,
        updateFetchApplicationDataFlag,
    ]);

    return waiting ? <LoaderProgress fullScreen /> : <>{props.children}</>;
};

export default Protected;
